<template>
  <AdminShell
    :page-title="campaignTitle"
    page-subtitle="查看当前推广的全部账单记录，并从账单进入支付。"
    active-menu="promotion"
  >
    <template #actions>
      <el-button plain @click="goToList">返回列表</el-button>
      <el-button plain @click="goToEdit" :disabled="!campaignId">编辑推广</el-button>
      <el-button type="primary" plain @click="goToTotalBills">推广总账单</el-button>
    </template>

    <section class="promotion-bills-page">
      <div class="bill-summary-grid">
        <article class="bill-summary-card">
          <span>当前预算</span>
          <strong>{{ budgetText(campaign || {}) }}</strong>
        </article>
        <article class="bill-summary-card">
          <span>最近账单</span>
          <strong>{{ latestBillStatus }}</strong>
        </article>
        <article class="bill-summary-card">
          <span>账单总数</span>
          <strong>{{ summaryText('total_orders') }}</strong>
        </article>
        <article class="bill-summary-card">
          <span>已支付金额</span>
          <strong>{{ paidAmountText }}</strong>
        </article>
      </div>

      <article class="promotion-bills-card">
        <el-table
          v-loading="loading"
          :data="orders"
          row-key="id"
          :row-class-name="rowClassName"
          empty-text="暂无账单"
        >
          <el-table-column label="账单号" min-width="140">
            <template slot-scope="{ row }">
              <div :id="rowAnchorId(row.id)" class="order-anchor">
                <strong>#{{ row.id }}</strong>
                <span>{{ row.order_no }}</span>
              </div>
            </template>
          </el-table-column>

          <el-table-column label="创建时间" width="170">
            <template slot-scope="{ row }">
              <span>{{ formatDateTime(row.created_at) }}</span>
            </template>
          </el-table-column>

          <el-table-column label="预算快照" min-width="170">
            <template slot-scope="{ row }">
              <span>{{ billingSnapshotText(row) }}</span>
            </template>
          </el-table-column>

          <el-table-column label="结算金额" min-width="140">
            <template slot-scope="{ row }">
              <span>{{ formatMoney(row.settlement_amount, row.settlement_currency) }}</span>
            </template>
          </el-table-column>

          <el-table-column label="支付通道" width="120">
            <template slot-scope="{ row }">
              <span>{{ channelLabel(row.channel) }}</span>
            </template>
          </el-table-column>

          <el-table-column label="状态" width="120">
            <template slot-scope="{ row }">
              <span class="status-chip" :class="`is-${row.status}`">{{ paymentStatusLabel(row.status) }}</span>
            </template>
          </el-table-column>

          <el-table-column label="支付时间" width="170">
            <template slot-scope="{ row }">
              <span>{{ row.paid_at ? formatDateTime(row.paid_at) : '--' }}</span>
            </template>
          </el-table-column>

          <el-table-column label="操作" width="260" fixed="right">
            <template slot-scope="{ row }">
              <div class="action-group">
                <el-button
                  v-if="isPayableStatus(row.status)"
                  type="text"
                  @click="goToCheckout(row)"
                >
                  {{ row.status === 'created' ? '去支付' : '继续支付' }}
                </el-button>
                <el-button
                  v-if="isRecreatableStatus(row.status)"
                  type="text"
                  :loading="recreatingOrderId === row.id"
                  @click="recreateOrder(row)"
                >
                  重新生成账单
                </el-button>
              </div>
            </template>
          </el-table-column>
        </el-table>
      </article>
    </section>
  </AdminShell>
</template>

<script>
import {
  createPromotionPaymentOrder,
  getPromotionCampaign,
  getUserInfo,
  listCampaignPromotionPaymentOrders,
} from '@/api'
import AdminShell from '@/components/admin/AdminShell.vue'
import { findPromotionPaymentChannel, promotionPaymentStatusLabel } from '@/constants/promotion-payment'
import {
  formatDateTime,
  formatMoney,
  formatPromotionBudgetText,
  isPayableStatus,
  isRecreatableStatus,
  resolveCommercialId,
} from '@/utils/promotion-form'

export default {
  name: 'PromotionCampaignBillsPage',
  components: {
    AdminShell,
  },
  data() {
    return {
      loading: false,
      recreatingOrderId: null,
      commercialId: '',
      campaign: null,
      latestPayment: null,
      summary: null,
      orders: [],
    }
  },
  computed: {
    campaignId() {
      return Number(this.$route.params.id || 0)
    },
    focusOrderId() {
      return Number(this.$route.query.focusOrderId || 0)
    },
    campaignTitle() {
      return this.campaign?.name || '推广账单明细'
    },
    latestBillStatus() {
      if (!this.latestPayment) return '暂无账单'
      return `${this.paymentStatusLabel(this.latestPayment.status)} · ${formatDateTime(this.latestPayment.created_at)}`
    },
    paidAmountText() {
      if (!this.summary) return '--'
      const currency = this.latestPayment?.settlement_currency || this.campaign?.currency || 'USD'
      return formatMoney(this.summary.total_paid_amount || 0, currency)
    },
  },
  async mounted() {
    await this.initialize()
  },
  watch: {
    '$route.query.focusOrderId'() {
      this.scrollToFocusOrder()
    },
  },
  methods: {
    async initialize() {
      await this.loadCommercialId()
      await this.fetchData()
      this.handleRouteFeedback()
      this.scrollToFocusOrder()
    },
    async loadCommercialId() {
      const { data } = await getUserInfo()
      this.commercialId = String(resolveCommercialId(data) || '')
    },
    async fetchData() {
      if (!this.commercialId || !this.campaignId) return
      this.loading = true
      try {
        const [campaignRes, ordersRes] = await Promise.all([
          getPromotionCampaign(this.campaignId, this.commercialId),
          listCampaignPromotionPaymentOrders(this.campaignId, {
            commercial_id: this.commercialId,
            limit: 100,
            offset: 0,
          }),
        ])
        this.campaign = campaignRes?.data || null
        this.latestPayment = ordersRes?.data?.latest_payment || null
        this.summary = ordersRes?.data?.summary || null
        this.orders = Array.isArray(ordersRes?.data?.items) ? ordersRes.data.items : []
      } catch (error) {
        this.$message.error(error.message || '加载推广账单失败')
      } finally {
        this.loading = false
      }
    },
    handleRouteFeedback() {
      const query = { ...(this.$route.query || {}) }
      let changed = false
      if (query.billCreated === '1') {
        this.$message.success(query.reused === '1' ? '已定位到待支付账单' : '账单已生成，请继续支付')
        delete query.billCreated
        delete query.reused
        changed = true
      }
      if (changed) {
        this.$router.replace({
          path: this.$route.path,
          query,
        }).catch(() => {})
      }
    },
    goToList() {
      this.$router.push('/promotion')
    },
    goToEdit() {
      this.$router.push(`/promotion/${this.campaignId}/edit`)
    },
    goToTotalBills() {
      this.$router.push('/promotion/bills')
    },
    goToCheckout(row) {
      this.$router.push({
        path: '/promotion/checkout',
        query: {
          campaignId: row.campaign_id,
          orderId: row.id,
        },
      })
    },
    async recreateOrder(row) {
      this.recreatingOrderId = row.id
      try {
        const res = await createPromotionPaymentOrder(this.campaignId, this.commercialId)
        const order = res?.data || {}
        this.$router.push({
          path: '/promotion/checkout',
          query: {
            campaignId: this.campaignId,
            orderId: order.id,
          },
        })
      } catch (error) {
        this.$message.error(error.message || '重新生成账单失败')
      } finally {
        this.recreatingOrderId = null
      }
    },
    rowClassName({ row }) {
      return Number(row.id) === this.focusOrderId ? 'is-focus-row' : ''
    },
    rowAnchorId(orderId) {
      return `promotion-order-${orderId}`
    },
    scrollToFocusOrder() {
      if (!this.focusOrderId) return
      this.$nextTick(() => {
        const element = document.getElementById(this.rowAnchorId(this.focusOrderId))
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      })
    },
    billingSnapshotText(row) {
      const snapshot = row.billing_snapshot || {}
      const budgetType = snapshot.budget_type === 'lifetime' ? '总预算' : '单日预算'
      return `${budgetType} ${String(snapshot.currency || row.original_currency || 'USD').toUpperCase()} ${Number(snapshot.budget_amount || row.original_amount || 0).toFixed(2)}`
    },
    channelLabel(code) {
      return (findPromotionPaymentChannel(code) || {}).label || code || '--'
    },
    budgetText: formatPromotionBudgetText,
    paymentStatusLabel: promotionPaymentStatusLabel,
    summaryText(key) {
      return this.summary ? Number(this.summary[key] || 0).toLocaleString() : '--'
    },
    formatDateTime,
    formatMoney,
    isPayableStatus,
    isRecreatableStatus,
  },
}
</script>

<style scoped>
.promotion-bills-page {
  display: grid;
  gap: 18px;
}

.bill-summary-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
}

.bill-summary-card,
.promotion-bills-card {
  border: 1px solid #dbe4f0;
  border-radius: 22px;
  background: rgba(255, 255, 255, 0.96);
}

.bill-summary-card {
  padding: 20px;
  display: grid;
  gap: 10px;
}

.bill-summary-card span {
  color: #64748b;
  font-size: 13px;
}

.bill-summary-card strong {
  color: #0f172a;
  font-size: 20px;
}

.promotion-bills-card {
  overflow: hidden;
}

.order-anchor {
  display: grid;
  gap: 4px;
}

.order-anchor strong {
  color: #0f172a;
}

.order-anchor span {
  color: #64748b;
  font-size: 12px;
}

.action-group {
  display: flex;
  align-items: center;
  gap: 10px;
}

.status-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 76px;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
}

.status-chip.is-created,
.status-chip.is-pending {
  color: #9a3412;
  background: #ffedd5;
}

.status-chip.is-processing {
  color: #1d4ed8;
  background: #dbeafe;
}

.status-chip.is-paid {
  color: #166534;
  background: #dcfce7;
}

.status-chip.is-failed,
.status-chip.is-cancelled,
.status-chip.is-expired {
  color: #991b1b;
  background: #fee2e2;
}

:deep(.el-table .is-focus-row) {
  background: rgba(59, 130, 246, 0.08);
}

@media (max-width: 960px) {
  .bill-summary-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
